import { ColorPickerPanel, LineMarker, SvgHelper } from 'markerjs2';
import { SmallResizeGrip } from './small_resize_grip';
import { perpendicularLineDistance, regularLineDistance, pointOnMainLine } from './line_functions';

/** Color for the damage point */
const DAMAGE_POINT_COLOR = '#29A7F4';
/** Offset if marker is created without dragging the line */
const DAMAGE_POINT_OFFSET = 25;
/** Default marker line color */
const DEFAULT_COLOR = '#fff';
/** Stroke width for the marker line */
const STROKE_WIDTH = 2.1;

export class DamageMarker extends LineMarker {
  /**
   * The grip added to the damage calculation point
   */
  curveGrip = null;

  /**
   * The point of damage (not on the main line)
   */
  curveX = 0;
  curveY = 0;

  /**
   * The perpendicular point of intersection from the damage point
   */
  intersectionX = 0;
  intersectionY = 0;

  manipulationStartCurveX = 0;
  manipulationStartCurveY = 0;

  damageLineDistance = 0;
  mainLineDistance = 0;

  lastGoodCurveX = 0;
  lastGoodCurveY = 0;

  strokeDasharray;

  constructor(container, overlayContainer, settings) {
    super(container, overlayContainer, settings);

    this.setStrokeColor = this.setStrokeColor.bind(this);
    this.setStrokeWidth = this.setStrokeWidth.bind(this);
    this.setStrokeDasharray = this.setStrokeDasharray.bind(this);
    this.positionGrips = this.positionGrips.bind(this);
    this.addControlGrips = this.addControlGrips.bind(this);
    this.adjustVisual = this.adjustVisual.bind(this);
    this.resize = this.resize.bind(this);

    this.strokeColor = DEFAULT_COLOR;
    this.strokeWidth = STROKE_WIDTH;
    this.strokeDasharray = settings.defaultStrokeDasharray;

    this.strokePanel = new ColorPickerPanel('Line color', settings.defaultColorSet, settings.defaultColor);
    this.strokePanel.onColorChanged = this.setStrokeColor;
  }

  /**
   * Returns true if passed SVG element belongs to the marker. False otherwise.
   *
   * @param el - target element.
   */
   ownsTarget(el) {
    if (super.ownsTarget(el) || el === this.visual || el === this.visibleLine ||
        el === this.selectorDamageLine || this.curveGrip.ownsTarget(el)) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Get the three points of the damage marker
   */
  getPoints() {
    return `${this.x1},${this.y1} ${this.curveX},${this.curveY} ${this.x2},${this.y2}`;
  }

  /**
   * Handles pointer (mouse, touch, stylus, etc.) down event.
   *
   * @param point - event coordinates.
   * @param target - direct event target element.
   */
  pointerDown(point, target = null) {
    super.pointerDown(point, target);

    this.manipulationStartCurveX = this.curveX;
    this.manipulationStartCurveY = this.curveY;

    if (this.state === 'creating') {
      this.setCurveXY(point.x + DAMAGE_POINT_OFFSET, point.y);
    }

    if (this.state === 'new') {
      this.newVisual(point.x, point.y);
    } else if (this.currentGripSelected(target)) {
      this.activeGrip = this.curveGrip;
      this._state = 'resize';
    }
  }

  newVisual(x, y) {
    this.setCurveXY(x, y);

    if (!this.x1 || !this.y1) return;

    this.createVisual();

    this._state = 'creating';
  }

  currentGripSelected(target) {
    return this.curveGrip && this.curveGrip.ownsTarget(target);
  }

  setCurveXY(x, y) {
    this.curveX = x;
    this.curveY = y;
  }

  /**
   * Create the marker visual
   */
  createVisual() {
    this.visual = SvgHelper.createGroup();
    this.visibleLine = SvgHelper.createLine(this.x1, this.y1, this.x2, this.y2, [
      ['stroke', this.strokeColor],
      ['stroke-width', this.strokeWidth.toString()],
    ]);

    this.selectorDamageLine = SvgHelper.createLine(this.x1, this.y1, this.x2, this.y2, [
      ['stroke', 'transparent'],
      ['stroke-width', this.strokeWidth.toString()],
    ]);

    this.visual.appendChild(this.visibleLine);
    this.visual.appendChild(this.selectorDamageLine);

    this.addMarkerVisualToContainer(this.visual);
  }

  /**
   * Adjusts visual after manipulation.
   */
  adjustVisual() {
    if (this.visibleLine && this.selectorDamageLine) {
      this.setVisibleLine();

      if (this.curveX && this.curveY) {
        // Store a known point where the damage marker is still perpendicular to
        // the main line, so we can restore it if the damage point is moved too far
        if (pointOnMainLine(this, ...this.perpendicularPoint())) {
          this.lastGoodCurveX = this.curveX;
          this.lastGoodCurveY = this.curveY;
        }
        this.setSelectorLine();
      }

      this.drawLine();
    }
  }

  drawLine() {
    SvgHelper.setAttributes(this.visibleLine, [['stroke', this.strokeColor]]);
    SvgHelper.setAttributes(this.visibleLine, [['stroke-width', this.strokeWidth.toString()]]);
    SvgHelper.setAttributes(this.visibleLine, [['stroke-dasharray', this.strokeDasharray.toString()]]);

    SvgHelper.setAttributes(this.selectorDamageLine, [['stroke', this.strokeColor]]);
    SvgHelper.setAttributes(this.selectorDamageLine, [['stroke-width', (this.strokeWidth - 0.25).toString()]]);
    SvgHelper.setAttributes(this.selectorDamageLine, [['stroke-dasharray', '3']]);

    this.damageLineDistance = perpendicularLineDistance(this);
    this.mainLineDistance = regularLineDistance(this);
  }

  setVisibleLine() {
    this.visibleLine.setAttribute('x1', this.x1.toString());
    this.visibleLine.setAttribute('y1', this.y1.toString());
    this.visibleLine.setAttribute('x2', this.x2.toString());
    this.visibleLine.setAttribute('y2', this.y2.toString());
  }

  setSelectorLine() {
    this.selectorDamageLine.setAttribute('x1', this.perpendicularPoint()[0].toString());
    this.selectorDamageLine.setAttribute('y1', this.perpendicularPoint()[1].toString());
    this.selectorDamageLine.setAttribute('x2', this.curveX.toString());
    this.selectorDamageLine.setAttribute('y2', this.curveY.toString());
  }

  /**
   * Creates manipulation grip.
   * @returns - manipulation grip.
   */
  createGrip(color = DEFAULT_COLOR) {
    const grip = new SmallResizeGrip(color);
    grip.visual.transform.baseVal.appendItem(SvgHelper.createTransform());
    this.controlBox.appendChild(grip.visual);

    return grip;
  }

  /**
   * Add manipulation grips to the control box.
   */
  addControlGrips() {
    // Set a default if the user just clicks once, instead of dragging the two points of the line
    this.setDefaults();

    if (this.x1 === 0 && this.y1 === 0 && this.x2 === 0 && this.y2 === 0) return;

    if (!this.grip1) this.grip1 = this.createGrip();
    if (!this.grip2) this.grip2 = this.createGrip();

    if (!this.curveGrip && this.curveX && this.curveY !== 0) {
      this.curveGrip = this.createGrip(DAMAGE_POINT_COLOR);
    }
    this.positionGrips();
  }

  setDefaults() {
    this.curveX = this.curveX || (this.x1 + DAMAGE_POINT_OFFSET);
    this.curveY = this.curveY || this.y1;
  }

  /**
   * Positions manipulation grips.
   */
  positionGrips() {
    if (!this.curveGrip) {
      this.curveGrip = this.createGrip(DAMAGE_POINT_COLOR);

      this.addControlGrips();
    }
    super.positionGrips();
    const gripSize = this.curveGrip.GRIP_SIZE;

    this.addPositionGrip(this.curveGrip.visual, this.curveX, this.curveY, gripSize);
    this.addPositionGrip(this.grip1.visual, this.x1, this.y1, gripSize);
    this.addPositionGrip(this.grip2.visual, this.x2, this.y2, gripSize);
  }

  addPositionGrip(visual, x, y, gripSize) {
    this.positionGrip(visual, x - gripSize / 2, y - gripSize / 2);
  }

  /**
   * Moves or resizes the marker.
   * @param point event coordinates
   */
  manipulate(point) {
    if (this.state === 'move') {
      this.curveX = this.manipulationStartCurveX + point.x - this.manipulationStartX;
      this.curveY = this.manipulationStartCurveY + point.y - this.manipulationStartY;
    }
    super.manipulate(point);
  }

  /**
   * Resizes the marker.
   * @param point event coordinates.
   */
  resize(point) {
    if (this.activeGrip === this.curveGrip) {
      this.curveX = point.x;
      this.curveY = point.y;
    }
    super.resize(point);
    if (this.state === 'creating') {
      this.curveX = this.x1 + (this.x2 - this.x1) / 2;
      this.curveY = this.y1 + (this.y2 - this.y1) / 2;
    }
  }

  /**
   * Sets line color.
   * @param color - new color.
   */
  setStrokeColor(color) {
    this.strokeColor = color;
    this.adjustVisual();
    this.colorChanged(color);
  }

  /**
   * Sets line width.
   * @param width - new width.
   */
  setStrokeWidth(width) {
    this.strokeWidth = width;
    this.adjustVisual();
  }

  /**
   * Sets line dash array.
   * @param dashes - new dash array.
   */
  setStrokeDasharray(dashes) {
    this.strokeDasharray = dashes;
    this.adjustVisual();
  }

  /**
   * Scales marker. Used after the image resize.
   *
   * @param scaleX - horizontal scale
   * @param scaleY - vertical scale
   */
  scale(scaleX, scaleY) {
    this.curveX = this.curveX * scaleX;
    this.curveY = this.curveY * scaleY;
    super.scale(scaleX, scaleY);
  }

  /**
   * Returns the list of toolbox panels for this marker type.
   * For the damage calculation, we just set the color/stroke panel.
   */
  get toolboxPanels() {
    return [this.strokePanel];
  }

  /**
   * Returns current marker state that can be restored in the future.
   */
  getState() {
    const result = Object.assign({
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      strokeDasharray: this.strokeDasharray,
      curveX: this.curveX,
      curveY: this.curveY,
      intersectionX: this.intersectionX,
      intersectionY: this.intersectionY,
      damageLineDistance: perpendicularLineDistance(this),
      mainLineDistance: regularLineDistance(this)
    }, super.getState());
    result.typeName = DamageMarker.typeName;

    return result;
  }

  /**
   * Restores previously saved marker state.
   *
   * @param state - previously saved state.
   */
  restoreState(state) {
    const lmState = state;
    this.strokeColor = lmState.strokeColor;
    this.strokeWidth = lmState.strokeWidth;
    this.strokeDasharray = lmState.strokeDasharray;
    this.curveX = lmState.curveX;
    this.curveY = lmState.curveY;
    this.intersectionX = lmState.intersectionX;
    this.intersectionY = lmState.intersectionY;
    super.restoreState(state);
  }

  /**
   * Work out the perpendicular point where the point of damage intersects the main line
   * @returns The [x, y] coordinate
   */
  perpendicularPoint() {
    var x1 = this.x1, y1 = this.y1, x2 = this.x2, y2 = this.y2, x3 = this.curveX, y3 = this.curveY;
    var px = x2 - x1, py = y2 - y1, dAB = px * px + py * py;
    var u = ((x3 - x1) * px + (y3 - y1) * py) / dAB;
    var x = x1 + u * px, y = y1 + u * py;

    if (pointOnMainLine(this, x, y)) {
      this.intersectionX = x;
      this.intersectionY = y;
      return [x, y];
    } else {
      if (this.activeGrip === this.curveGrip) {
        // Stop moving the damage point if it's no longer perpendicular to the main line
        this.curveX = this.lastGoodCurveX;
        this.curveY = this.lastGoodCurveY;
      } else {
        // Place the damage point in the middle of the main line if the main line is moved
        // beyond a point where the damage point can be perpendicular to the main line
        this.curveX = (this.x2 + this.x1) / 2;
        this.curveY = (this.y2 + this.y1) / 2;
      }
      return [this.intersectionX, this.intersectionY]
    }
  }
}

DamageMarker.typeName = 'Damage Marker';
DamageMarker.title = 'Damage Calculation marker';
DamageMarker.icon = `<svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M20.3 3.70609C20.3 4.53787 19.6257 5.21217 18.7939 5.21217C17.9621 5.21217 17.2878 4.53787 17.2878 3.70609C17.2878 2.87431 17.9621 2.20001 18.7939 2.20001C19.6257 2.20001 20.3 2.87431 20.3 3.70609Z" fill="white"/>
<path d="M5.7121 18.2939C5.7121 19.1257 5.03781 19.8 4.20603 19.8C3.37424 19.8 2.69995 19.1257 2.69995 18.2939C2.69995 17.4621 3.37424 16.7878 4.20603 16.7878C5.03781 16.7878 5.7121 17.4621 5.7121 18.2939Z" fill="white"/>
<path d="M16.593 4.86584L5.36646 16.0923C5.80978 16.3264 6.17386 16.6904 6.40798 17.1337L17.6343 5.90743C17.191 5.67329 16.827 5.30918 16.593 4.86584Z" fill="white"/>
</svg>`
